// apps/admin/src/features/billing/PaymentRequestCreateForm.tsx
//
// Phase 8.2 Wave 4 Task 26 — Pre-payment Request Create form.
//
// vg-binding: CONTEXT:decisions=P8.D-236,P8.D-237,P8.D-238,P8.D-239,P8.D-242,P8.D-243
// vg-binding: PHASE:08.2-admin-finance-catalog
// vg-binding: INTERFACE-STANDARDS:error-shape
//
//  the create form composes:
//   1. Merchant picker (search-driven dropdown) — uses shared <MerchantPicker>.
//   2. Merchant info auto-fill + per-field toggle.
//        default ON  → Name / Phone / Email
//        default OFF → Business Name / Tax ID / Business Address / Zip Code
//      Toggle ON + empty field → BE skips that line on PDF (last
//      sentence). Toggle is the inclusion flag — the PDF only renders
//      what's toggled-ON and non-empty.
//   3. Legal Entity picker — auto-selects entity assigned to merchant,
//      falls back to system-default entity (step 3).
//   4. Bank account dropdown — defaults to primary bank of the chosen
//      legal entity (step 4).
//   5. Amount — positive USD only, formatted $xx.xx, no min/max guardrail
//       step 5.
//   6. Description — auto-filled "Topup to Printway Wallet", required,
//      max 500 chars (step 6).
//
// Submit: POST /api/v1/admin/payment-requests with Idempotency-Key
// (task-25 backend endpoint). Server enforces CREATE_PAYMENT_REQUEST perm
//  + audit-log entry. Immutable post-create — no edit path
// in this form.
//
// Test IDs (per task-26 spec):
//   payment-request-create-form, payment-request-merchant-select,
//   payment-request-legal-entity-select, payment-request-bank-select,
//   payment-request-amount-input, payment-request-description-input,
//   payment-request-toggle-name-checkbox

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';

import { apiClient } from '@printway/shared/api';
import {
  Button,
  Dialog,
  DialogHeader,
  DialogTitle,
  Input,
  MerchantPicker,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';
import {
  envelopeError,
  newIdempotencyKey,
  type LooseClient,
} from '../../pages/billing/billing-shared.js';

// ---------------------------------------------------------------------------
// Wire shapes — slim mirror of BE schemas (task-25 backend ships in parallel).
// ---------------------------------------------------------------------------

interface MerchantInfoSnapshot {
  name: string;
  phone: string;
  email: string;
  business_name: string | null;
  tax_id: string | null;
  business_address: string | null;
  zip_code: string | null;
}

/**
 * Pull the legal-entity rows out of whatever nesting the list endpoint used.
 *
 * dbg-20260725-uat-fin-2629-2632 (#2632): the live shape is
 * `{ success, data: { data: [...], pagination } }` — an envelope around a
 * paginated payload that itself uses the key `data`. Rather than hard-code one
 * more shape, descend through the known container keys until an array turns up.
 */
function extractEntityRows(payload: unknown): LegalEntityOption[] {
  const CONTAINER_KEYS = ['data', 'entities', 'items', 'rows'] as const;
  let node: unknown = payload;
  // Depth 4 is generous: envelope → page payload → array. The guard only exists
  // so a malformed self-referencing payload can't spin here.
  for (let depth = 0; depth < 4; depth += 1) {
    if (Array.isArray(node)) return node as LegalEntityOption[];
    if (!node || typeof node !== 'object') return [];
    const rec = node as Record<string, unknown>;
    const nextKey = CONTAINER_KEYS.find((k) => rec[k] !== undefined && rec[k] !== null);
    if (!nextKey) return [];
    node = rec[nextKey];
  }
  return Array.isArray(node) ? (node as LegalEntityOption[]) : [];
}

interface LegalEntityOption {
  id: string;
  name: string;
  is_default: boolean;
  /** Bank accounts attached to this entity (Task 24 subform). */
  bank_accounts?: Array<{
    id: string;
    bank_name: string;
    account_holder?: string | null;
    account_number?: string | null;
    is_primary?: boolean;
  }>;
}

interface BankAccountOption {
  id: string;
  bank_name: string;
  label: string;
  is_primary: boolean;
}

/** Per-field merchant-info toggles. Order matches PDF rendering. */
const FIELD_KEYS = [
  'name',
  'phone',
  'email',
  'business_name',
  'tax_id',
  'business_address',
  'zip_code',
] as const;
type FieldKey = (typeof FIELD_KEYS)[number];

/**  default toggle state — ON for Name/Phone/Email, OFF for the rest. */
const DEFAULT_TOGGLES: Record<FieldKey, boolean> = {
  name: true,
  phone: true,
  email: true,
  business_name: false,
  tax_id: false,
  business_address: false,
  zip_code: false,
};

const DEFAULT_DESCRIPTION = 'Topup to Printway Wallet';
const MAX_DESCRIPTION_LEN = 500;

/** Radix sentinel for the "no bank account" selectable option (maps to ''). */
const BANK_NONE_VALUE = '__none__';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatAddress(addr: unknown): string {
  if (!addr || typeof addr !== 'object') return '';
  const a = addr as {
    street?: string;
    city?: string;
    region?: string;
    country?: string;
    postal_code?: string;
  };
  return [a.street, a.city, a.region, a.country]
    .filter((p): p is string => Boolean(p && p.trim()))
    .join(', ');
}

function extractMerchantInfo(raw: unknown): MerchantInfoSnapshot {
  if (!raw || typeof raw !== 'object') {
    return {
      name: '',
      phone: '',
      email: '',
      business_name: null,
      tax_id: null,
      business_address: null,
      zip_code: null,
    };
  }
  const env = raw as { data?: unknown };
  const m = (env.data && typeof env.data === 'object' ? env.data : raw) as Record<
    string,
    unknown
  >;
  const address = m.business_address as
    | { street?: string; city?: string; region?: string; postal_code?: string; country?: string }
    | null
    | undefined;
  return {
    name: String(m.name ?? '') || '',
    phone: String(m.phone ?? '') || '',
    email: String(m.email ?? '') || '',
    business_name: typeof m.business_name === 'string' ? m.business_name : null,
    tax_id: typeof m.tax_id === 'string' ? m.tax_id : null,
    business_address: address ? formatAddress(address) : null,
    zip_code: address?.postal_code ?? null,
  };
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PaymentRequestCreateFormProps {
  open: boolean;
  onClose: () => void;
  /** Notify parent after a successful create so the list can refetch. */
  onCreated?: (payload: { id: string; pr_number: string }) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PaymentRequestCreateForm({
  open,
  onClose,
  onCreated,
}: PaymentRequestCreateFormProps): JSX.Element | null {
  const { toast } = useToast();
  const { t } = useTranslation('finance');

  // --- Merchant ---
  const [merchantId, setMerchantId] = useState<string | null>(null);
  const [merchantLabel, setMerchantLabel] = useState<string | null>(null);
  const [merchantInfo, setMerchantInfo] = useState<MerchantInfoSnapshot | null>(
    null,
  );
  const [merchantLoading, setMerchantLoading] = useState(false);
  // Per-field toggle map — defaults applied on mount.
  const [toggles, setToggles] = useState<Record<FieldKey, boolean>>(
    DEFAULT_TOGGLES,
  );

  // --- Legal Entity + Bank ---
  const [entities, setEntities] = useState<LegalEntityOption[]>([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [legalEntityId, setLegalEntityId] = useState<string>('');
  const [bankAccountId, setBankAccountId] = useState<string>('');

  // --- Amount + Description ---
  const [amountStr, setAmountStr] = useState('');
  const [description, setDescription] = useState(DEFAULT_DESCRIPTION);

  // --- Submission state ---
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset form whenever the dialog reopens — keeps state isolation across
  // sequential create flows (immutable means we never reopen for edit).
  useEffect(() => {
    if (!open) return;
    setMerchantId(null);
    setMerchantLabel(null);
    setMerchantInfo(null);
    setToggles(DEFAULT_TOGGLES);
    setLegalEntityId('');
    setBankAccountId('');
    setAmountStr('');
    setDescription(DEFAULT_DESCRIPTION);
    setError(null);
    setSubmitting(false);
  }, [open]);

  // Fetch entities list on open — caches across form sessions implicitly by
  // staying in component state; explicit invalidation on close keeps it
  // fresh enough for an admin workflow.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setEntitiesLoading(true);
    void (async (): Promise<void> => {
      try {
        const client = apiClient() as unknown as LooseClient;
        const { data, response } = await client.GET(
          '/api/v1/admin/legal-entities?limit=100',
        );
        if (cancelled || !response.ok) return;
        // dbg-20260725-uat-fin-2629-2632 (#2632 bước 5–7): the endpoint answers
        //   { success, data: { data: LegalEntityOption[], pagination }, requestId }
        // (legal-entity.admin.service.ts returns `{ data, pagination }`, wrapped
        // again by the handler's ok() envelope). The old unwrap only understood
        // a bare array, `data`, and `data.entities` — never `data.data` — so
        // `rows` was ALWAYS [], the Pháp nhân dropdown always read "Chưa có
        // pháp nhân nào", legalEntityId stayed null and canSubmit could never
        // become true. No Payment Request was creatable on ANY environment.
        // Walk the known nesting keys instead of enumerating shapes.
        const rows = extractEntityRows(data);
        setEntities(rows);
      } finally {
        if (!cancelled) setEntitiesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Fetch merchant info when the picker selection changes. Also resolves
  // the merchant's assigned legal entity (auto-select step 3) +
  // primary bank (auto-select step 4).
  useEffect(() => {
    if (!open || !merchantId) {
      setMerchantInfo(null);
      return;
    }
    let cancelled = false;
    setMerchantLoading(true);
    void (async (): Promise<void> => {
      try {
        const client = apiClient() as unknown as LooseClient;
        const { data, response } = await client.GET(
          `/api/v1/admin/merchants/${encodeURIComponent(merchantId)}`,
        );
        if (cancelled || !response.ok) return;
        const info = extractMerchantInfo(data);
        setMerchantInfo(info);

        //  step 3: legal entity auto-select. Prefer merchant's
        // assigned LE if returned by BE (`legal_entity_id`), else default.
        const body = data as
          | { data?: { legal_entity_id?: string | null } }
          | { legal_entity_id?: string | null }
          | undefined;
        const assignedLEId =
          (body && 'data' in body && body.data?.legal_entity_id) ||
          (body as { legal_entity_id?: string | null } | undefined)?.legal_entity_id ||
          null;
        if (assignedLEId) {
          setLegalEntityId(String(assignedLEId));
        } else {
          const def = entities.find((e) => e.is_default);
          if (def) setLegalEntityId(def.id);
        }
      } finally {
        if (!cancelled) setMerchantLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, merchantId, entities]);

  // Derived: bank-account options for the currently-selected legal entity.
  const bankOptions: BankAccountOption[] = useMemo(() => {
    const selected = entities.find((e) => e.id === legalEntityId);
    const banks = selected?.bank_accounts ?? [];
    return banks.map((b) => ({
      id: b.id,
      bank_name: b.bank_name,
      label: [b.bank_name, b.account_number?.slice(-4)]
        .filter(Boolean)
        .join(' · '),
      is_primary: Boolean(b.is_primary),
    }));
  }, [entities, legalEntityId]);

  // Auto-pick primary bank whenever the entity changes (step 4).
  useEffect(() => {
    if (bankOptions.length === 0) {
      setBankAccountId('');
      return;
    }
    const primary = bankOptions.find((b) => b.is_primary);
    setBankAccountId(primary?.id ?? bankOptions[0]!.id);
  }, [bankOptions]);

  // Amount parsing — positive USD cents.
  const amountCents = useMemo(() => {
    const parsed = parseFloat(amountStr);
    if (!Number.isFinite(parsed) || parsed <= 0) return 0;
    return Math.round(parsed * 100);
  }, [amountStr]);

  const toggleField = useCallback((key: FieldKey) => {
    setToggles((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const canSubmit =
    !submitting &&
    Boolean(merchantId) &&
    Boolean(legalEntityId) &&
    amountCents > 0 &&
    description.trim().length > 0 &&
    description.length <= MAX_DESCRIPTION_LEN;

  async function submit(): Promise<void> {
    if (!merchantId) {
      setError(t('payment_request.form.merchantRequired'));
      return;
    }
    if (!legalEntityId) {
      setError(t('payment_request.form.legalEntityRequired'));
      return;
    }
    if (amountCents <= 0) {
      setError(t('payment_request.form.amountPositive'));
      return;
    }
    if (!description.trim()) {
      setError(t('payment_request.form.descriptionRequired'));
      return;
    }
    if (description.length > MAX_DESCRIPTION_LEN) {
      setError(t('payment_request.form.descriptionMax', { max: MAX_DESCRIPTION_LEN }));
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const client = apiClient() as unknown as LooseClient;
      // dbg-20260725-uat-fin-2629-2632 (#2632 bước 7): this payload never
      // matched POST /api/v1/admin/payment-requests, so create ALWAYS 400'd:
      //   • `amount_usd_cents` — schema requires `amount_cents`
      //   • `merchant_info_toggle` — schema requires `merchant_info_toggles`
      //   • `merchant_info` — rejected outright (additionalProperties: false);
      //     the server composes the snapshot itself from Merchant +
      //     BillingAddress, which is the point of an immutable PR
      //   • `bank_account_id: null` — required, min length 1
      // Until the multi-bank legal-entity work (Task 22) ships, the admin list
      // endpoint exposes no bank_accounts[], so bankOptions is empty and the
      // server's canonical id for the entity's single bank slot IS the entity
      // id — exactly what its `bank_account_id !== String(entity._id)` guard
      // compares against.
      const body = {
        merchant_id: merchantId,
        legal_entity_id: legalEntityId,
        bank_account_id: bankAccountId || legalEntityId,
        amount_cents: amountCents,
        description: description.trim(),
        // Which snapshot fields to print on the PDF. The server freezes the
        // VALUES itself; this map only says which lines to render. Keys are
        // `show_<field>` on the wire (MerchantInfoTogglesSchema) while local
        // state is keyed by bare field name — map, don't send raw.
        merchant_info_toggles: Object.fromEntries(
          FIELD_KEYS.map((k) => [`show_${k}`, toggles[k]]),
        ),
      };
      const res = await client.POST('/api/v1/admin/payment-requests', {
        body,
        headers: { 'Idempotency-Key': newIdempotencyKey() },
      });
      if (!res.response.ok) {
        const message =
          envelopeError(res.error ?? res.data) ||
          `HTTP ${res.response.status}`;
        setError(message);
        return;
      }
      const payload = res.data as
        | { data?: { id?: string; pr_number?: string } }
        | { id?: string; pr_number?: string }
        | undefined;
      const inner =
        payload && typeof payload === 'object' && 'data' in payload && payload.data
          ? payload.data
          : (payload as { id?: string; pr_number?: string } | undefined);
      const id = String(inner?.id ?? '');
      const prNumber = String(inner?.pr_number ?? '');
      toast({
        title: t('payment_request.form.createSuccess'),
        description: prNumber
          ? t('payment_request.form.createSuccessDesc', { prNumber })
          : t('payment_request.form.createSuccessFallback'),
        variant: 'success',
      });
      onCreated?.({ id, pr_number: prNumber });
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('payment_request.networkError'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      className="max-w-2xl"
      data-testid="payment-request-create-form"
    >
      <DialogHeader>
        <DialogTitle>{t('payment_request.form.title')}</DialogTitle>
      </DialogHeader>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="max-h-[75vh] space-y-4 overflow-y-auto px-6 py-4"
      >
        {/* Step 1 — Merchant picker (step 1) */}
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-pw-text">
            {t('payment_request.columns.merchant')} <span className="text-red-600 dark:text-red-300">*</span>
          </span>
          <MerchantPicker
            value={merchantId}
            valueLabel={merchantLabel}
            onChange={(id, label) => {
              setMerchantId(id);
              setMerchantLabel(label);
            }}
            testId="payment-request-merchant-select"
          />
        </label>

        {/* Step 2 — Merchant info auto-fill + per-field toggle (step 2) */}
        {merchantId ? (
          <fieldset className="rounded-md border border-pw-border bg-pw-surface/40 p-3">
            <legend className="px-1 text-xs font-medium uppercase tracking-wide text-pw-text-muted">
              {t('payment_request.form.merchantInfoLegend')}
            </legend>
            {merchantLoading ? (
              <p className="text-xs text-pw-text-muted">{t('payment_request.form.loadingMerchant')}</p>
            ) : (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {FIELD_KEYS.map((key) => {
                  const value = merchantInfo?.[key] ?? '';
                  const checked = toggles[key];
                  return (
                    <label
                      key={key}
                      className="flex items-start gap-2 text-xs"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleField(key)}
                        className="mt-0.5 h-4 w-4 rounded border-pw-border text-pw-primary focus:ring-pw-primary/30"
                        data-testid={`payment-request-toggle-${key.replace(/_/g, '-')}-checkbox`}
                      />
                      <span className="flex flex-col">
                        <span className="font-medium text-pw-text">
                          {t(`payment_request.fields.${key}`)}
                        </span>
                        <span className="break-all text-pw-text-muted">
                          {value || (
                            <em className="italic text-pw-text-muted/70">
                              {t('payment_request.form.empty')}
                            </em>
                          )}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            <p className="mt-2 text-[11px] text-pw-text-muted">
              {t('payment_request.form.toggleHint')}
            </p>
          </fieldset>
        ) : null}

        {/* Step 3 — Legal Entity (step 3, fallback to default) */}
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-pw-text">
            {t('payment_request.columns.legalEntity')} <span className="text-red-600 dark:text-red-300">*</span>
          </span>
          <Select
            value={legalEntityId || undefined}
            onValueChange={(value) => setLegalEntityId(value)}
            disabled={entitiesLoading || entities.length === 0}
          >
            <SelectTrigger data-testid="payment-request-legal-entity-select">
              <SelectValue
                placeholder={
                  entitiesLoading
                    ? t('payment_request.form.loadingShort')
                    : entities.length === 0
                      ? t('payment_request.form.noLegalEntities')
                      : t('payment_request.form.selectLegalEntity')
                }
              />
            </SelectTrigger>
            <SelectContent>
              {entities.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.name}
                  {e.is_default ? t('payment_request.form.defaultSuffix') : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>

        {/* Step 4 — Bank account dropdown (step 4, defaults to primary) */}
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-pw-text">
            {t('payment_request.form.bankAccount')}
          </span>
          <Select
            value={bankAccountId === '' ? BANK_NONE_VALUE : bankAccountId}
            onValueChange={(value) =>
              setBankAccountId(value === BANK_NONE_VALUE ? '' : value)
            }
            disabled={bankOptions.length === 0}
          >
            <SelectTrigger data-testid="payment-request-bank-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={BANK_NONE_VALUE}>
                {bankOptions.length === 0
                  ? t('payment_request.form.selectLegalEntityFirst')
                  : t('payment_request.form.selectBankAccount')}
              </SelectItem>
              {bankOptions.map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  {b.label}
                  {b.is_primary ? t('payment_request.form.primarySuffix') : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>

        {/* Step 5 — Amount (step 5, positive USD, no min/max) */}
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-pw-text">
            {t('payment_request.form.amount')} <span className="text-red-600 dark:text-red-300">*</span>
          </span>
          <Input
            type="number"
            inputMode="decimal"
            min="0.01"
            step="0.01"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            placeholder="0.00"
            data-testid="payment-request-amount-input"
          />
        </label>

        {/* Step 6 — Description (step 6, default Topup..., max 500) */}
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-pw-text">
            {t('payment_request.form.description')} <span className="text-red-600 dark:text-red-300">*</span>
          </span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            maxLength={MAX_DESCRIPTION_LEN}
            className="block w-full rounded-md border border-pw-border bg-pw-bg p-2 text-sm focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary/20"
            data-testid="payment-request-description-input"
          />
          <span className="mt-1 block text-[11px] text-pw-text-muted">
            {t('payment_request.form.descriptionHelp', {
              count: description.length,
              max: MAX_DESCRIPTION_LEN,
              default: DEFAULT_DESCRIPTION,
            })}
          </span>
        </label>

        {error ? (
          <div
            role="alert"
            className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-2 text-sm text-red-800 dark:text-red-300"
            data-testid="payment-request-create-error"
          >
            {error}
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="secondary"
            onClick={onClose}
            type="button"
            disabled={submitting}
          >
            {t('payment_request.form.cancel')}
          </Button>
          <Button
            variant="default"
            type="submit"
            disabled={!canSubmit}
            data-testid="payment-request-create-submit"
          >
            {submitting
              ? t('payment_request.form.creating')
              : t('payment_request.form.submit')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export default PaymentRequestCreateForm;
